import React, { useState, useEffect, useRef } from 'react';
import Header from './Header';
import Footer from './Footer';
import { Users, Shield, Zap, CheckCircle, Coffee, ChevronDown, ChevronUp, Calendar, Clock, X, Code } from 'lucide-react';

const diferenciais = [
  {
    icon: Users,
    title: 'Suporte personalizado e atendimento especializado',
    desc: 'Nossa equipe acompanha cada etapa do seu projeto, garantindo atendimento próximo e soluções sob medida.'
  },
  {
    icon: Shield,
    title: 'Infraestrutura otimizada para desempenho e segurança',
    desc: 'Utilizamos as melhores práticas de desenvolvimento e hospedagem para garantir performance e proteção.'
  },
  {
    icon: Zap,
    title: 'Processo ágil com entregas em etapas',
    desc: 'Você acompanha o progresso do seu site em cada fase, com entregas rápidas e feedback contínuo.'
  },
  {
    icon: CheckCircle,
    title: 'Manutenção e suporte pós-lançamento',
    desc: 'Após a entrega, seguimos juntos com suporte técnico e melhorias contínuas.'
  },
];

const etapas = [
  {
    title: '1. Consultoria Inicial Gratuita',
    desc: 'Entendemos seu negócio, objetivos e necessidades para propor a melhor solução.'
  },
  {
    title: '2. Proposta Personalizada',
    desc: 'Você recebe um plano detalhado, cronograma e orçamento transparente.'
  },
  {
    title: '3. Desenvolvimento Colaborativo',
    desc: 'Acompanhamento em tempo real, entregas parciais e feedback constante.'
  },
  {
    title: '4. Lançamento e Suporte',
    desc: 'Seu site no ar com monitoramento, manutenção e suporte contínuo.'
  },
];

const faqs = [
  {
    q: 'Posso pedir alterações durante o desenvolvimento?',
    a: 'Sim! Nosso processo é totalmente colaborativo. Você pode sugerir ajustes e melhorias em cada etapa do projeto. Queremos garantir que o resultado final seja exatamente o que você imaginou, por isso incentivamos feedbacks constantes e revisões sempre que necessário.'
  },
  {
    q: 'Meu site será responsivo e rápido?',
    a: 'Com certeza! Todos os sites que desenvolvemos são otimizados para funcionar perfeitamente em celulares, tablets e computadores. Utilizamos as melhores práticas de performance e SEO para garantir carregamento rápido, navegação fluida e excelente experiência para seus visitantes.'
  },
  {
    q: 'Vocês oferecem manutenção após o lançamento?',
    a: 'Sim, oferecemos planos de manutenção e suporte contínuo. Após o lançamento, você pode contar com nossa equipe para atualizações, correções, melhorias e suporte técnico sempre que precisar. Assim, seu site estará sempre seguro, atualizado e funcionando perfeitamente.'
  },
  {
    q: 'Quais tecnologias vocês utilizam?',
    a: 'Trabalhamos com as stacks mais modernas do mercado, como React, Node.js, Next.js, bancos de dados escaláveis e integrações com APIs. Escolhemos sempre a tecnologia mais adequada para o seu projeto, visando performance, segurança e facilidade de manutenção.'
  },
  {
    q: 'Como funciona o pagamento?',
    a: 'O pagamento é flexível e pode ser feito por etapas do projeto, conforme o avanço e sua aprovação. Assim, você tem mais segurança e controle sobre o investimento, pagando apenas pelo que já foi entregue e aprovado.'
  },
  {
    q: 'Vocês fazem integração com sistemas externos?',
    a: 'Sim! Realizamos integrações com ERPs, CRMs, gateways de pagamento, APIs de terceiros, sistemas de automação e muito mais. Se você já utiliza alguma ferramenta ou sistema, podemos conectar tudo para facilitar sua rotina.'
  },
  {
    q: 'Posso migrar meu site atual para a Hostul?',
    a: 'A migração gratuita está incluída nos planos Profissional e Hostul Max. Nossa equipe orienta a transferência, e as condições e a disponibilidade são confirmadas pelo suporte antes do início.'
  },
  {
    q: 'O site terá painel de controle?',
    a: 'Sim, desenvolvemos painéis personalizados para facilitar a gestão do seu site. Você poderá atualizar conteúdos, acompanhar estatísticas, gerenciar usuários e muito mais, tudo de forma simples e intuitiva.'
  },
];

function FAQAccordion() {
  const [openIndex, setOpenIndex] = useState(null);
  return (
    <div className="flex flex-col items-center gap-4">
      {faqs.map((faq, idx) => (
        <div key={idx} className="w-full max-w-2xl bg-white/10 border border-gray-700 rounded-xl overflow-hidden shadow-lg">
          <button
            className="w-full flex justify-between items-center px-6 py-5 text-left focus:outline-none hover:bg-purple-900/20 transition-colors"
            onClick={() => setOpenIndex(openIndex === idx ? null : idx)}
            aria-expanded={openIndex === idx}
          >
            <span className="text-lg font-semibold text-white">{faq.q}</span>
            {openIndex === idx ? <ChevronUp size={22} className="text-purple-400" /> : <ChevronDown size={22} className="text-purple-400" />}
          </button>
          {openIndex === idx && (
            <div className="px-6 pb-6 text-gray-300 animate-fade-in text-lg leading-relaxed">
              {faq.a}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function CalendarCustom({ value, onChange, min, highlightDates = [], onClose }) {
  const today = new Date();
  const todayStr = today.toISOString().split('T')[0];
  // Corrigir bug: sempre mostrar o mês da data selecionada ao abrir
  const [month, setMonth] = useState(() => {
    const d = value ? new Date(value) : new Date();
    d.setDate(1);
    return d;
  });
  // Atualizar mês exibido sempre que value mudar
  React.useEffect(() => {
    if (value) {
      const d = new Date(value);
      setMonth(new Date(d.getFullYear(), d.getMonth(), 1));
    }
  }, [value]);
  // Corrigir bug de seleção de data (fuso horário):
  function toISODateString(date) {
    return date.toISOString().split('T')[0];
  }
  function isSameDayISO(date, isoStr) {
    return toISODateString(date) === isoStr;
  }
  function isDisabled(date) {
    if (date.getDay() === 0) return true; // domingo
    if (min && toISODateString(date) < min) return true;
    return false;
  }
  function getDayClass(date) {
    if (isDisabled(date)) return 'bg-gray-800 text-gray-500 border-gray-700 cursor-not-allowed';
    if (value && isSameDayISO(date, value)) return 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white border-purple-500 scale-105 shadow-lg';
    if (isSameDayISO(date, todayStr)) return 'border-2 border-purple-400 text-purple-300 font-bold';
    if (highlightDates.some(d => isSameDayISO(date, toISODateString(d)))) return 'border-2 border-indigo-400 text-indigo-300 font-bold';
    return 'bg-gray-900 text-gray-200 border-gray-700 hover:bg-purple-900/30 hover:border-purple-400';
  }
  function getMonthDays(year, monthIdx) {
    const last = new Date(year, monthIdx + 1, 0);
    const days = [];
    for (let i = 1; i <= last.getDate(); i++) {
      days.push(new Date(year, monthIdx, i));
    }
    return days;
  }
  const days = getMonthDays(month.getFullYear(), month.getMonth());
  const startDay = days[0].getDay();
  const weeks = [];
  let week = Array(startDay).fill(null);
  days.forEach((d, idx) => {
    week.push(d);
    if (week.length === 7) {
      weeks.push(week);
      week = [];
    }
  });
  if (week.length) while (week.length < 7) week.push(null);
  if (week.some(Boolean)) weeks.push(week);
  function prevMonth(e) {
    if (e) e.preventDefault();
    setMonth(m => new Date(m.getFullYear(), m.getMonth() - 1, 1));
  }
  function nextMonth(e) {
    if (e) e.preventDefault();
    setMonth(m => new Date(m.getFullYear(), m.getMonth() + 1, 1));
  }
  // Calcular se pode voltar mês
  const isPrevDisabled = month.getFullYear() < today.getFullYear() || (month.getFullYear() === today.getFullYear() && month.getMonth() <= today.getMonth());
  return (
    <div className="bg-gray-800 rounded-xl p-4 border border-gray-700 w-full max-w-xs mx-auto z-50 shadow-2xl">
      <div className="flex justify-between items-center mb-2">
        <button
          type="button"
          onClick={isPrevDisabled ? undefined : prevMonth}
          disabled={isPrevDisabled}
          className={`text-purple-400 hover:text-purple-200 px-2 py-1 rounded focus:outline-none transition-all ${isPrevDisabled ? 'opacity-40 cursor-not-allowed' : ''}`}
        >&#8592;</button>
        <span className="font-semibold text-white">{month.toLocaleString('pt-BR', { month: 'long', year: 'numeric' })}</span>
        <button type="button" onClick={nextMonth} className="text-purple-400 hover:text-purple-200 px-2 py-1 rounded focus:outline-none">&#8594;</button>
      </div>
      <div className="grid grid-cols-7 gap-1 mb-1 text-xs text-purple-300 text-center">
        {['D','S','T','Q','Q','S','S'].map(d => <div key={d}>{d}</div>)}
      </div>
      {weeks.map((w, i) => (
        <div key={i} className="grid grid-cols-7 gap-1 mb-1">
          {w.map((d, j) => d ? (
            <button
              key={j}
              type="button"
              disabled={isDisabled(d)}
              onClick={() => { if (!isDisabled(d)) { onChange(toISODateString(d)); if (onClose) onClose(); }}}
              className={`w-8 h-8 rounded-lg border-2 flex items-center justify-center font-semibold text-sm transition-all ${getDayClass(d)}`}
              aria-label={d.toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'short' })}
            >
              {d.getDate()}
            </button>
          ) : <div key={j}></div>)}
        </div>
      ))}
      <div className="flex justify-between mt-2">
        <button
          type="button"
          onClick={() => { onChange(toISODateString(today)); if (onClose) onClose(); }}
          className="text-xs bg-purple-700 text-white px-2 py-1 rounded hover:bg-purple-800 transition-all"
        >Hoje</button>
        <button
          type="button"
          onClick={() => { const amanha = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1); onChange(toISODateString(amanha)); if (onClose) onClose(); }}
          className="text-xs bg-indigo-700 text-white px-2 py-1 rounded hover:bg-indigo-800 transition-all"
        >Amanhã</button>
      </div>
    </div>
  );
}

function HorarioPopup({ open, onClose, horarios, value, onSelect }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-gray-900 rounded-2xl shadow-2xl p-6 w-full max-w-xs relative animate-fade-in">
        <button onClick={onClose} className="absolute top-3 right-3 text-gray-400 hover:text-purple-400"><X size={22} /></button>
        <h2 className="text-lg font-bold text-purple-300 mb-4 flex items-center gap-2"><Clock size={20}/> Selecione o horário</h2>
        <div className="grid grid-cols-3 gap-3">
          {horarios.map(h => (
            <button
              key={h.value}
              type="button"
              disabled={h.disabled}
              onClick={() => { if (!h.disabled) { onSelect(h.value); onClose(); }}}
              className={`py-2 rounded-lg border-2 font-semibold text-sm transition-all
                ${h.disabled ? 'bg-gray-800 border-gray-700 text-gray-500 cursor-not-allowed' :
                  value === h.value ? 'bg-gradient-to-r from-purple-600 to-indigo-600 border-purple-500 text-white scale-105 shadow-lg' :
                  'bg-gray-900 border-gray-700 text-gray-200 hover:bg-purple-900/30 hover:border-purple-400'}
              `}
            >
              {h.value}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function AgendamentoPopup({ open, onClose }) {
  const [step, setStep] = useState(0); // 0: dados, 1: projeto, 2: data/hora, 3: resumo
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [project, setProject] = useState('');
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const now = new Date();
  const todayStr = now.toISOString().split('T')[0];
  const tomorrow = new Date(now.getTime() + 24 * 60 * 60 * 1000);
  const tomorrowStr = tomorrow.toISOString().split('T')[0];
  // Função para obter o nome do dia da semana em português
  function getWeekdayName(date) {
    const dias = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'];
    const d = typeof date === 'string' ? new Date(date + 'T12:00:00') : date;
    return dias[d.getDay()];
  }
  // Ajustar função isSunday para receber Date ou string
  function isSunday(date) {
    if (!date) return false;
    const d = typeof date === 'string' ? new Date(date + 'T12:00:00') : date;
    return d.getDay() === 0;
  }
  // Gera horários de 30 em 30 minutos
  const horarios = [];
  for (let h = 8; h <= 18; h++) {
    for (let m = 0; m < 60; m += 30) {
      if (h === 18 && m > 30) break;
      const hour = h.toString().padStart(2, '0');
      const min = m.toString().padStart(2, '0');
      const value = `${hour}:${min}`;
      let disabled = false;
      if (date === todayStr) {
        const slot = new Date();
        slot.setHours(h, m, 0, 0);
        if (slot < now) disabled = true;
      }
      horarios.push({ value, disabled });
    }
  }
  const validateEmail = (email) => /.+@.+\..+/.test(email);
  const validatePhone = (phone) => /^\d{10,}$/.test(phone.replace(/\D/g, ''));
  const handleNext = () => {
    setError('');
    if (step === 0) {
      if (!name || !email || !phone) {
        setError('Preencha todos os campos.');
        return;
      }
      if (!validateEmail(email)) {
        setError('Email inválido.');
        return;
      }
      if (!validatePhone(phone)) {
        setError('Telefone inválido. Use DDD + número.');
        return;
      }
    }
    if (step === 1) {
      if (!project) {
        setError('Descreva seu projeto.');
        return;
      }
    }
    if (step === 2) {
      if (!date || !time) {
        setError('Escolha o dia e horário.');
        return;
      }
    }
    setStep(step + 1);
  };
  const handleBack = () => {
    setError('');
    setStep(step - 1);
  };
  const handleSubmit = (e) => {
    e.preventDefault();
    setSuccess(true);
    setTimeout(() => {
      setSuccess(false);
      onClose();
      setStep(0);
      setName(''); setEmail(''); setPhone(''); setDate(''); setTime(''); setProject('');
    }, 2000);
  };
  const [calendarOpen, setCalendarOpen] = useState(false);
  const calendarRef = useRef();
  const [horarioOpen, setHorarioOpen] = useState(false);

  React.useEffect(() => {
    if (!calendarOpen) return;
    function handleClick(e) {
      if (calendarRef.current && !calendarRef.current.contains(e.target)) {
        setCalendarOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [calendarOpen]);

  if (!open) return null;
  const steps = [
    { label: 'Seus Dados', icon: <Users size={22} className="text-purple-400" /> },
    { label: 'Sobre o Projeto', icon: <Code size={22} className="text-purple-400" /> },
    { label: 'Data & Horário', icon: <Calendar size={22} className="text-purple-400" /> },
    { label: 'Resumo', icon: <CheckCircle size={22} className="text-green-400" /> }
  ];
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="bg-gray-900 rounded-2xl shadow-2xl p-0 w-full max-w-lg relative animate-fade-in">
        <button onClick={onClose} className="absolute top-3 right-3 text-gray-400 hover:text-purple-400"><X size={24} /></button>
        {/* Etapas do wizard */}
        <div className="flex items-center justify-center gap-2 pt-8 pb-4">
          {steps.map((s, idx) => (
            <div key={s.label} className={`flex flex-col items-center flex-1 transition-all duration-200 ${step === idx ? 'scale-110' : 'opacity-50 scale-95'}`}>
              <div className={`rounded-full w-10 h-10 flex items-center justify-center border-2 transition-all duration-200 ${step === idx ? 'border-purple-500 bg-purple-900/40 shadow-lg' : 'border-gray-700 bg-gray-800'}`}>{s.icon}</div>
              <span className={`mt-2 text-xs font-semibold transition-all duration-200 ${step === idx ? 'text-purple-300' : 'text-gray-400'}`}>{s.label}</span>
            </div>
          ))}
        </div>
        <div className="px-8 pb-8 pt-2">
          {success ? (
            <div className="text-green-400 text-lg font-semibold text-center py-8">Agendamento realizado com sucesso!<br/>Entraremos em contato no horário escolhido.</div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {step === 0 && (
                <div className="space-y-4 animate-fade-in">
                  <h2 className="text-xl font-bold text-purple-300 mb-2">Seus Dados</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-gray-300 mb-2">Nome</label>
                      <input type="text" required value={name} onChange={e => setName(e.target.value)} className="w-full px-4 py-2 rounded-lg bg-gray-800 border border-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-purple-500" placeholder="Seu nome" />
                    </div>
                    <div>
                      <label className="block text-gray-300 mb-2">Telefone</label>
                      <input type="tel" required value={phone} onChange={e => setPhone(e.target.value)} className="w-full px-4 py-2 rounded-lg bg-gray-800 border border-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-purple-500" placeholder="(DDD) 99999-9999" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-gray-300 mb-2">Email</label>
                    <input type="email" required value={email} onChange={e => setEmail(e.target.value)} className="w-full px-4 py-2 rounded-lg bg-gray-800 border border-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-purple-500" placeholder="seu@email.com" />
                  </div>
                </div>
              )}
              {step === 1 && (
                <div className="space-y-4 animate-fade-in">
                  <h2 className="text-xl font-bold text-purple-300 mb-2">Sobre o Projeto</h2>
                  <label className="block text-gray-300 mb-2">Descreva o projeto que deseja desenvolver</label>
                  <textarea required value={project} onChange={e => setProject(e.target.value)} className="w-full px-4 py-2 rounded-lg bg-gray-800 border border-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-purple-500 min-h-[100px] resize-y" placeholder="Conte um pouco sobre sua ideia, objetivo, público-alvo, funcionalidades desejadas..."></textarea>
                </div>
              )}
              {step === 2 && (
                <div className="space-y-4 animate-fade-in">
                  <h2 className="text-xl font-bold text-purple-300 mb-2">Escolha o Dia e Horário</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-gray-300 mb-2">Dia</label>
                      <button
                        type="button"
                        onClick={() => setCalendarOpen(true)}
                        className={`w-full flex items-center justify-between px-4 py-2 rounded-lg border-2 ${date ? 'border-purple-500 bg-gray-900 text-white' : 'border-gray-700 bg-gray-800 text-gray-400'} focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all`}
                      >
                        <span className="flex items-center gap-2">
                          <Calendar size={20} className="text-purple-400" />
                          {date ? new Date(date + 'T12:00:00').toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' }) : 'Selecione o dia'}
                        </span>
                        <ChevronDown size={18} className="ml-2" />
                      </button>
                      <CalendarPopup
                        open={calendarOpen}
                        onClose={() => setCalendarOpen(false)}
                        value={date}
                        onChange={d => { setDate(d); setTime(''); setCalendarOpen(false); }}
                        min={todayStr}
                      />
                      {date && isSunday(date) && (
                        <div className="text-red-400 text-xs mt-2">Domingos não estão disponíveis para agendamento.</div>
                      )}
                      {date && !isSunday(date) && date !== todayStr && date !== tomorrowStr && (
                        <div className="text-xs text-purple-300 mt-2">{getWeekdayName(date)}</div>
                      )}
                    </div>
                    <div>
                      <label className="block text-gray-300 mb-2">Horário</label>
                      <button
                        type="button"
                        onClick={() => setHorarioOpen(true)}
                        className={`w-full flex items-center justify-between px-4 py-2 rounded-lg border-2 ${time ? 'border-purple-500 bg-gray-900 text-white' : 'border-gray-700 bg-gray-800 text-gray-400'} focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all`}
                      >
                        <span className="flex items-center gap-2">
                          <Clock size={20} className="text-purple-400" />
                          {time ? time : 'Selecione o horário'}
                        </span>
                        <ChevronDown size={18} className="ml-2" />
                      </button>
                      <HorarioPopup
                        open={horarioOpen}
                        onClose={() => setHorarioOpen(false)}
                        horarios={horarios}
                        value={time}
                        onSelect={h => setTime(h)}
                      />
                      {time && <div className="text-xs text-purple-300 mt-2">Horário selecionado: <span className="font-bold">{time}</span></div>}
                    </div>
                  </div>
                </div>
              )}
              {step === 3 && (
                <div className="space-y-4 animate-fade-in">
                  <h2 className="text-xl font-bold text-purple-300 mb-2">Resumo do Agendamento</h2>
                  <div className="bg-gray-800 rounded-xl p-4 border border-gray-700 text-gray-200">
                    <div className="mb-2"><span className="font-semibold text-white">Nome:</span> {name}</div>
                    <div className="mb-2"><span className="font-semibold text-white">Telefone:</span> {phone}</div>
                    <div className="mb-2"><span className="font-semibold text-white">Email:</span> {email}</div>
                    <div className="mb-2"><span className="font-semibold text-white">Projeto:</span> {project}</div>
                    <div className="mb-2"><span className="font-semibold text-white">Dia:</span> {date}</div>
                    <div><span className="font-semibold text-white">Horário:</span> {time}</div>
                  </div>
                </div>
              )}
              {error && <div className="text-red-400 text-sm text-center font-semibold animate-fade-in">{error}</div>}
              <div className="flex justify-between gap-4 pt-2">
                {step > 0 && (
                  <button type="button" onClick={handleBack} className="px-6 py-2 rounded-lg bg-gray-800 border border-gray-700 text-gray-300 font-semibold hover:bg-gray-700 transition-all">Voltar</button>
                )}
                {step < 3 && (
                  <button type="button" onClick={handleNext} className="ml-auto px-8 py-2 rounded-lg bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-bold hover:scale-105 transition-transform">Avançar</button>
                )}
                {step === 3 && (
                  <button type="submit" className="ml-auto px-8 py-2 rounded-lg bg-gradient-to-r from-green-500 to-emerald-600 text-white font-bold hover:scale-105 transition-transform flex items-center gap-2"><CheckCircle size={18}/> Confirmar</button>
                )}
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

// Popup centralizado para o calendário
function CalendarPopup({ open, onClose, ...props }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-gray-900 rounded-2xl shadow-2xl p-6 w-full max-w-xs relative animate-fade-in">
        <button onClick={onClose} className="absolute top-3 right-3 text-gray-400 hover:text-purple-400"><X size={22} /></button>
        <CalendarCustom {...props} onClose={onClose} />
      </div>
    </div>
  );
}

// Remover HeaderAndFooterWrapper e usar apenas o conteúdo da página
export default function EngenhariaSitesPage() {
  const [agendarOpen, setAgendarOpen] = useState(false);
  const [show, setShow] = useState(false);
  useEffect(() => {
    window.scrollTo(0, 0);
    setShow(true);
  }, []);
  return (
    <>
      <Header />
      <div className={`min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 text-white transition-all duration-700 ${show ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'} ease-out`}>
        {/* Conteúdo principal */}
        <section className="pt-32 md:pt-40 pb-16 md:pb-24 relative overflow-hidden">
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight bg-gradient-to-r from-purple-400 to-indigo-400 text-transparent bg-clip-text">
                Engenharia de Sites: Desenvolvimento Avançado 100% Personalizado
              </h1>
              <p className="text-xl md:text-2xl text-gray-300 mb-8 leading-relaxed">
                Sites exclusivos, feitos sob medida para suas necessidades, com código limpo, alta performance e segurança. Atendimento direto com especialistas para garantir uma solução profissional, escalável e confiável.
              </p>
              <button
                onClick={() => setAgendarOpen(true)}
                className="inline-block bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-lg font-bold px-8 py-4 rounded-xl shadow-lg hover:scale-105 transition-transform"
              >
                Agende sua consultoria gratuita
              </button>
            </div>
          </div>
        </section>
        {/* Jornada do Cliente */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-10 text-purple-300">Como funciona a Engenharia de Sites?</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8 max-w-5xl mx-auto">
              {etapas.map((etapa, idx) => (
                <div key={idx} className="bg-white/10 border border-gray-700 rounded-xl p-6 flex flex-col items-center text-center">
                  <span className="text-3xl font-bold text-purple-400 mb-2">{etapa.title.split('.')[0]}</span>
                  <h3 className="text-lg font-semibold text-white mb-2">{etapa.title.slice(3)}</h3>
                  <p className="text-gray-300 text-base">{etapa.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
        {/* Diferenciais */}
        <section className="py-16 bg-gradient-to-r from-purple-900/60 to-indigo-900/60">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-10 text-purple-300">Diferenciais Hostul</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {diferenciais.map((dif, idx) => (
                <div key={idx} className="bg-white/10 border border-gray-700 rounded-xl p-6 flex items-start gap-4">
                  <dif.icon size={32} className="text-purple-400 mt-1" />
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-1">{dif.title}</h3>
                    <p className="text-gray-300 text-base">{dif.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
        {/* Público-alvo */}
        <section className="py-16">
          <div className="container mx-auto px-4 max-w-3xl">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-6 text-purple-300">Para quem é?</h2>
            <p className="text-gray-200 text-lg text-center mb-4">
              Pequenas empresas, e-commerces, agências e iniciantes que buscam um site único, com suporte dedicado e acompanhamento próximo.
            </p>
            <ul className="flex flex-wrap justify-center gap-4 text-gray-300 text-base">
              <li className="bg-white/10 px-4 py-2 rounded-lg">Empreendedores</li>
              <li className="bg-white/10 px-4 py-2 rounded-lg">Lojas Virtuais</li>
              <li className="bg-white/10 px-4 py-2 rounded-lg">Agências Digitais</li>
              <li className="bg-white/10 px-4 py-2 rounded-lg">Profissionais Liberais</li>
              <li className="bg-white/10 px-4 py-2 rounded-lg">Projetos Inovadores</li>
            </ul>
          </div>
        </section>
        {/* Depoimento */}
        <section className="py-16 bg-gradient-to-r from-purple-900/60 to-indigo-900/60">
          <div className="container mx-auto px-4 max-w-2xl text-center">
            <div className="flex flex-col items-center">
              <div className="bg-gradient-to-br from-purple-500 to-indigo-600 w-16 h-16 rounded-full flex items-center justify-center mb-4">
                <Coffee size={32} className="text-white" />
              </div>
              <blockquote className="text-xl text-gray-200 italic mb-2">“A Hostul entregou um site incrível para minha empresa, com atendimento próximo e suporte rápido. Recomendo para quem quer algo realmente profissional!”</blockquote>
              <span className="text-purple-300 font-semibold">Juliana Souza, CEO de Startup</span>
            </div>
          </div>
        </section>
        {/* FAQ Moderno */}
        <section className="py-16">
          <div className="container mx-auto px-4 max-w-3xl">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-8 text-purple-300">Perguntas Frequentes</h2>
            <FAQAccordion />
          </div>
        </section>
        {/* CTA final com popup de agendamento */}
        <section id="consultoria" className="py-16 bg-gradient-to-r from-purple-900 to-indigo-900">
          <div className="container mx-auto px-4 max-w-2xl text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Pronto para transformar sua ideia em um site exclusivo?</h2>
            <p className="text-xl text-gray-200 mb-10">Agende uma consultoria gratuita e descubra como podemos criar o site perfeito para o seu negócio.</p>
            <div className="flex justify-center">
              <button onClick={() => setAgendarOpen(true)} className="inline-block bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-lg font-bold px-10 py-4 rounded-xl shadow-lg hover:scale-105 transition-transform flex items-center gap-2">
                <Calendar size={22}/> Agendar Consultoria
              </button>
            </div>
            <p className="mt-6 text-gray-300">Atendimento rápido e sem compromisso</p>
          </div>
        </section>
        <Footer />
      </div>
      <AgendamentoPopup open={agendarOpen} onClose={() => setAgendarOpen(false)} />
    </>
  );
} 
